import { escapeCsvValue } from './csvValidator';

export const DEFAULT_MAX_SIZE_MB = 10;
const ERROR_CSV_HEADERS = ['lineNumber', 'errorType', 'field', 'value', 'allowedOptions'];
const DUPLICATE_FILE_ERROR_CODE = 'DUPLICATE_FILE_DETECTED';

const FILE_LEVEL_ERROR_TYPES = new Set([
  'HEADER_MISMATCH',
  'NO_DATA_ROWS',
  'FILE_TOO_LARGE',
  'EMPTY_FILE',
]);

const resolveFatalErrorLineNumber = (fatalError) => {
  if (!fatalError) {
    return null;
  }
  if (FILE_LEVEL_ERROR_TYPES.has(fatalError.type)) {
    return -1;
  }
  if (Number.isFinite(fatalError.lineNumber) && fatalError.lineNumber > 0) {
    return fatalError.lineNumber;
  }
  return -1;
};

const trimTrailingUrlPunctuation = (url) => url.replace(/[),.;!?]+$/g, '');

export const bytesFromMb = (value) => Math.round(Number(value || 0) * 1024 * 1024);

export const buildDefaultName = ({ fileName, existingNames = [] }) => {
  if (!fileName) {
    return '';
  }
  const normalizedExistingNames = new Set(
    existingNames
      .map((value) => String(value || '').trim())
      .filter(Boolean),
  );
  if (!normalizedExistingNames.has(fileName)) {
    return fileName;
  }

  let suffix = 1;
  let candidate = `${fileName}_${suffix}`;
  while (normalizedExistingNames.has(candidate)) {
    suffix += 1;
    candidate = `${fileName}_${suffix}`;
  }
  return candidate;
};

export const isFileTooLarge = (file, maxSizeBytes) => !!file && file.size > maxSizeBytes;

export const buildErrorCsvContent = (invalidRows = [], fatalError = null) => {
  const rows = [];
  if (fatalError) {
    rows.push({
      lineNumber: resolveFatalErrorLineNumber(fatalError),
      errorType: fatalError.type || 'FATAL_ERROR',
      field: '',
      value: fatalError.message || 'Unknown error.',
      allowedOptions: '',
    });
  }
  invalidRows.forEach((row) => {
    const lineNumber = row.lineNumber;
    (row.blankFields || []).forEach((field) => {
      rows.push({
        lineNumber,
        errorType: 'MISSING_FIELD',
        field,
        value: '',
        allowedOptions: '',
      });
    });
    (row.invalidOptions || []).forEach((invalid) => {
      const allowedOptions = Array.isArray(invalid.options)
        ? invalid.options.join('|')
        : '';
      rows.push({
        lineNumber,
        errorType: 'INVALID_OPTION',
        field: invalid.field,
        value: invalid.value,
        allowedOptions,
      });
    });
    (row.invalidFormats || []).forEach((invalid) => {
      rows.push({
        lineNumber,
        errorType: 'INVALID_FORMAT',
        field: invalid.field,
        value: invalid.value,
        allowedOptions: '',
      });
    });
  });

  const lines = [ERROR_CSV_HEADERS.join(',')];
  rows.forEach((row) => {
    const line = ERROR_CSV_HEADERS
      .map((header) => escapeCsvValue(row[header]))
      .join(',');
    lines.push(line);
  });
  return lines.join('\n');
};

export const parseDuplicateFileUrl = (message) => {
  const normalizedMessage = String(message || '');
  if (!normalizedMessage.includes(DUPLICATE_FILE_ERROR_CODE)) {
    return null;
  }

  const urlMatch = normalizedMessage.match(/https?:\/\/[^\s]+/i);
  if (!urlMatch) {
    return null;
  }

  const rawUrl = urlMatch[0];
  const parsedUrl = trimTrailingUrlPunctuation(rawUrl);
  if (!parsedUrl) {
    return null;
  }

  const rawUrlStart = normalizedMessage.indexOf(rawUrl);
  if (rawUrlStart < 0) {
    return null;
  }

  const rawUrlEnd = rawUrlStart + rawUrl.length;
  const rawTrailingChars = rawUrl.slice(parsedUrl.length);

  return {
    prefix: normalizedMessage.slice(0, rawUrlStart),
    url: parsedUrl,
    suffix: `${rawTrailingChars}${normalizedMessage.slice(rawUrlEnd)}`,
  };
};
